import {
  DEFAULT_LOCALE,
  LEGACY_LOCALE_SEGMENTS,
  LOCALES,
  ROUTE_LOCALE_KEYS,
  isLocaleCode,
  type LocaleCode,
} from "./locale-config";
import {
  BLOG_ENTITY_SLUGS,
  BRITISH_COLUMBIA_HUB_SLUGS,
  CAMPAIGN_ENTITY_SLUGS,
  DIRECTORY_SEGMENTS,
  LEGACY_ENGLISH_CANONICAL_PATHS,
  LEGACY_PROJECT_ENTITY_SLUGS,
  PROJECT_ENTITY_SLUGS,
  SERVICE_AREA_ENTITY_SLUGS,
  SERVICE_ENTITY_SLUGS,
  type DirectorySegmentKey,
} from "./route-slugs";

export type RouteId =
  | "home"
  | "core:services"
  | "core:service-areas"
  | "core:emergency-roofing"
  | "core:commercial-roofing"
  | "core:strata-roofing"
  | "core:residential-roofing"
  | "core:roof-systems"
  | "core:roof-estimate"
  | "core:british-columbia"
  | "core:before-after"
  | "core:blog"
  | "core:about"
  | "core:faq"
  | "core:contact"
  | "core:privacy"
  | "core:terms"
  | "core:thank-you"
  | `service:${string}`
  | `area:${string}`
  | `project:${string}`
  | `campaign:${string}`
  | `blog:${string}`;

export type ResolvedRoute = {
  routeId: RouteId;
  locale: LocaleCode;
};

export type ParsedLocalizedPath = {
  locale: LocaleCode;
  /** Path after locale prefix, always starts with `/` (or is `/` for home). */
  localizedRemainder: string;
  route: ResolvedRoute | null;
};

type PathLookupEntry = {
  routeId: RouteId;
  locale: LocaleCode;
};

const CORE_ROUTE_BY_SEGMENT: Record<DirectorySegmentKey, RouteId> = {
  services: "core:services",
  serviceAreas: "core:service-areas",
  emergencyRoofing: "core:emergency-roofing",
  commercialRoofing: "core:commercial-roofing",
  strataRoofing: "core:strata-roofing",
  residentialRoofing: "core:residential-roofing",
  roofSystems: "core:roof-systems",
  roofEstimate: "core:roof-estimate",
  beforeAfter: "core:before-after",
  blog: "core:blog",
  campaigns: "core:campaigns" as RouteId,
  about: "core:about",
  faq: "core:faq",
  contact: "core:contact",
  privacy: "core:privacy",
  terms: "core:terms",
  thankYou: "core:thank-you",
};

const CORE_ROUTE_IDS = new Set<RouteId>([
  "home",
  "core:services",
  "core:service-areas",
  "core:emergency-roofing",
  "core:commercial-roofing",
  "core:strata-roofing",
  "core:residential-roofing",
  "core:roof-systems",
  "core:roof-estimate",
  "core:british-columbia",
  "core:before-after",
  "core:blog",
  "core:about",
  "core:faq",
  "core:contact",
  "core:privacy",
  "core:terms",
  "core:thank-you",
]);

const pathLookup = new Map<string, PathLookupEntry>();

function registerPath(routeId: RouteId, locale: LocaleCode, segments: string[]) {
  const path = `/${locale}/${segments.join("/")}`.replace(/\/+$/, "") || `/${locale}`;
  const normalized = normalizePublicPath(path);
  pathLookup.set(normalized, { routeId, locale });
}

function buildLookupTable() {
  if (pathLookup.size > 0) return;

  for (const locale of ROUTE_LOCALE_KEYS) {
    registerPath("home", locale, []);

    for (const [key, routeId] of Object.entries(CORE_ROUTE_BY_SEGMENT)) {
      if (routeId === ("core:campaigns" as RouteId)) continue;
      const segment = DIRECTORY_SEGMENTS[key as DirectorySegmentKey][locale];
      registerPath(routeId, locale, [segment]);
    }

    const servicesDir = DIRECTORY_SEGMENTS.services[locale];
    for (const [entityId, slugs] of Object.entries(SERVICE_ENTITY_SLUGS)) {
      registerPath(`service:${entityId}`, locale, [servicesDir, slugs[locale]]);
    }

    const areasDir = DIRECTORY_SEGMENTS.serviceAreas[locale];
    registerPath("core:british-columbia", locale, [areasDir, BRITISH_COLUMBIA_HUB_SLUGS[locale]]);
    for (const [entityId, slugs] of Object.entries(SERVICE_AREA_ENTITY_SLUGS)) {
      registerPath(`area:${entityId}`, locale, [areasDir, slugs[locale]]);
    }

    const galleryDir = DIRECTORY_SEGMENTS.beforeAfter[locale];
    for (const [entityId, slugs] of Object.entries(PROJECT_ENTITY_SLUGS)) {
      registerPath(`project:${entityId}`, locale, [galleryDir, slugs[locale]]);
    }

    const campaignsDir = DIRECTORY_SEGMENTS.campaigns[locale];
    for (const [entityId, slugs] of Object.entries(CAMPAIGN_ENTITY_SLUGS)) {
      registerPath(`campaign:${entityId}`, locale, [campaignsDir, slugs[locale]]);
    }

    const blogDir = DIRECTORY_SEGMENTS.blog[locale];
    for (const [entityId, slugs] of Object.entries(BLOG_ENTITY_SLUGS)) {
      registerPath(`blog:${entityId}`, locale, [blogDir, slugs[locale]]);
    }
  }
}

/** Decode URI, NFC-normalize, and ensure a leading slash. */
export function normalizePublicPath(pathname: string): string {
  let decoded = pathname;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    decoded = pathname;
  }
  const nfc = decoded.normalize("NFC");
  if (!nfc) return "/";
  const withSlash = nfc.startsWith("/") ? nfc : `/${nfc}`;
  if (withSlash === "/") return withSlash;
  return withSlash.replace(/\/+$/, "");
}

/**
 * Percent-encode a redirect path for Location headers.
 * Decodes first so already-encoded segments are not double-encoded.
 */
export function encodeRedirectPath(pathname: string): string {
  let decoded = pathname;
  try {
    decoded = decodeURI(pathname);
  } catch {
    decoded = pathname;
  }
  return encodeURI(decoded);
}

function splitSegments(pathname: string): string[] {
  return normalizePublicPath(pathname).split("/").filter(Boolean);
}

/** Build the canonical localized public path for a route ID and locale. */
export function localizedPath(routeId: RouteId, locale: LocaleCode): string {
  if (routeId === "home") {
    return `/${locale}`;
  }

  if (routeId.startsWith("service:")) {
    const entityId = routeId.slice("service:".length);
    const slug = SERVICE_ENTITY_SLUGS[entityId]?.[locale];
    if (!slug) throw new Error(`Unknown service route: ${routeId}`);
    return `/${locale}/${DIRECTORY_SEGMENTS.services[locale]}/${slug}`;
  }

  if (routeId === "core:british-columbia") {
    return `/${locale}/${DIRECTORY_SEGMENTS.serviceAreas[locale]}/${BRITISH_COLUMBIA_HUB_SLUGS[locale]}`;
  }

  if (routeId.startsWith("area:")) {
    const entityId = routeId.slice("area:".length);
    const slug = SERVICE_AREA_ENTITY_SLUGS[entityId]?.[locale];
    if (!slug) throw new Error(`Unknown area route: ${routeId}`);
    return `/${locale}/${DIRECTORY_SEGMENTS.serviceAreas[locale]}/${slug}`;
  }

  if (routeId.startsWith("project:")) {
    const entityId = routeId.slice("project:".length);
    const slug = PROJECT_ENTITY_SLUGS[entityId]?.[locale];
    if (!slug) throw new Error(`Unknown project route: ${routeId}`);
    return `/${locale}/${DIRECTORY_SEGMENTS.beforeAfter[locale]}/${slug}`;
  }

  if (routeId.startsWith("campaign:")) {
    const entityId = routeId.slice("campaign:".length);
    const slug = CAMPAIGN_ENTITY_SLUGS[entityId]?.[locale];
    if (!slug) throw new Error(`Unknown campaign route: ${routeId}`);
    return `/${locale}/${DIRECTORY_SEGMENTS.campaigns[locale]}/${slug}`;
  }

  if (routeId.startsWith("blog:")) {
    const entityId = routeId.slice("blog:".length);
    const slug = BLOG_ENTITY_SLUGS[entityId]?.[locale];
    if (!slug) throw new Error(`Unknown blog route: ${routeId}`);
    return `/${locale}/${DIRECTORY_SEGMENTS.blog[locale]}/${slug}`;
  }

  const coreKey = Object.entries(CORE_ROUTE_BY_SEGMENT).find(([, id]) => id === routeId)?.[0] as
    DirectorySegmentKey | undefined;
  if (!coreKey) throw new Error(`Unknown route ID: ${routeId}`);
  return `/${locale}/${DIRECTORY_SEGMENTS[coreKey][locale]}`;
}

/** Resolve a public pathname to locale + route ID. */
export function resolveRouteFromPath(pathname: string): ParsedLocalizedPath | null {
  buildLookupTable();
  const normalized = normalizePublicPath(pathname);
  const entry = pathLookup.get(normalized);
  const segments = splitSegments(normalized);
  const localeSegment = segments[0]?.toLowerCase();

  if (!localeSegment || !isLocaleCode(localeSegment)) {
    return null;
  }

  const locale = localeSegment;
  const localizedRemainder = segments.length <= 1 ? "/" : `/${segments.slice(1).join("/")}`;

  if (entry) {
    return {
      locale: entry.locale,
      localizedRemainder,
      route: { routeId: entry.routeId, locale: entry.locale },
    };
  }

  return { locale, localizedRemainder, route: null };
}

/** Switch a route to another locale, preserving entity identity. */
export function switchLocalePath(currentPathname: string, targetLocale: LocaleCode): string | null {
  const resolved = resolveRouteFromPath(currentPathname);
  if (!resolved?.route) return localizedPath("home", targetLocale);
  return localizedPath(resolved.route.routeId, targetLocale);
}

/** Legacy English canonical path (e.g. `/services/foo`) → route ID. */
export function legacyEnglishPathToRouteId(canonicalPath: string): RouteId | null {
  const normalized = normalizePublicPath(canonicalPath);
  const core = LEGACY_ENGLISH_CANONICAL_PATHS[normalized];
  if (core) return core as RouteId;

  if (normalized.startsWith("/services/")) {
    const slug = normalized.slice("/services/".length);
    if (SERVICE_ENTITY_SLUGS[slug]) return `service:${slug}`;
  }
  if (normalized.startsWith("/service-areas/")) {
    const slug = normalized.slice("/service-areas/".length);
    if (SERVICE_AREA_ENTITY_SLUGS[slug]) return `area:${slug}`;
  }
  if (normalized.startsWith("/before-after/")) {
    const slug = normalized.slice("/before-after/".length);
    if (PROJECT_ENTITY_SLUGS[slug]) return `project:${slug}`;
    const legacyProject = LEGACY_PROJECT_ENTITY_SLUGS[slug];
    if (legacyProject && PROJECT_ENTITY_SLUGS[legacyProject]) {
      return `project:${legacyProject}`;
    }
  }
  if (normalized.startsWith("/campaigns/")) {
    const slug = normalized.slice("/campaigns/".length);
    if (CAMPAIGN_ENTITY_SLUGS[slug]) return `campaign:${slug}`;
  }
  if (normalized.startsWith("/blog/")) {
    const slug = normalized.slice("/blog/".length);
    if (BLOG_ENTITY_SLUGS[slug]) return `blog:${slug}`;
  }

  const bareService = normalized.replace(/^\//, "");
  if (SERVICE_ENTITY_SLUGS[bareService]) return `service:${bareService}`;

  return null;
}

export type RedirectResolution = {
  target: string;
  permanent: true;
};

/**
 * Returns a single-hop permanent redirect target for legacy URLs, or null if none.
 * Handles: prefixless English, old locale segments, and English paths under new locale keys.
 */
export function resolveLegacyRedirect(pathname: string): RedirectResolution | null {
  const normalized = normalizePublicPath(pathname);
  const segments = splitSegments(normalized);
  const first = segments[0]?.toLowerCase() ?? "";

  if (normalized === "/" || normalized === "") {
    return { target: localizedPath("home", DEFAULT_LOCALE), permanent: true };
  }

  if (isLocaleCode(first)) {
    const remainder = segments.length <= 1 ? "/" : `/${segments.slice(1).join("/")}`;
    const resolved = resolveRouteFromPath(normalized);
    if (resolved?.route) return null;

    const legacyRoute = legacyEnglishPathToRouteId(remainder);
    if (legacyRoute) {
      return { target: localizedPath(legacyRoute, first as LocaleCode), permanent: true };
    }
    return null;
  }

  if (LEGACY_LOCALE_SEGMENTS[first]) {
    const newLocale = LEGACY_LOCALE_SEGMENTS[first]!;
    const remainder = segments.length <= 1 ? "/" : `/${segments.slice(1).join("/")}`;
    const routeId = legacyEnglishPathToRouteId(remainder);
    if (routeId) {
      return { target: localizedPath(routeId, newLocale), permanent: true };
    }
    if (remainder === "/") {
      return { target: localizedPath("home", newLocale), permanent: true };
    }
    // Every former /en-us/ URL keeps the rest of its path under /en-ca/.
    // Unknown paths 404 on the Canadian URL instead of collapsing to home.
    if (first === "en-us") {
      const safe = remainder
        .split("/")
        .filter(Boolean)
        .every((segment) => segment !== "." && segment !== ".." && !segment.includes("\\"));
      if (safe) return { target: `/en-ca${remainder}`, permanent: true };
    }
    return { target: localizedPath("home", newLocale), permanent: true };
  }

  const routeId = legacyEnglishPathToRouteId(normalized);
  if (routeId) {
    return { target: localizedPath(routeId, DEFAULT_LOCALE), permanent: true };
  }

  return null;
}

export function routeIdToEnglishCanonicalPath(routeId: RouteId): string {
  if (routeId === "home") return "/";
  if (routeId.startsWith("service:")) {
    return `/services/${routeId.slice("service:".length)}`;
  }
  if (routeId.startsWith("area:")) {
    return `/service-areas/${routeId.slice("area:".length)}`;
  }
  if (routeId.startsWith("project:")) {
    return `/before-after/${routeId.slice("project:".length)}`;
  }
  if (routeId.startsWith("campaign:")) {
    return `/campaigns/${routeId.slice("campaign:".length)}`;
  }
  if (routeId.startsWith("blog:")) {
    return `/blog/${routeId.slice("blog:".length)}`;
  }
  const map: Partial<Record<RouteId, string>> = {
    "core:services": "/services",
    "core:service-areas": "/service-areas",
    "core:emergency-roofing": "/emergency-roofing",
    "core:commercial-roofing": "/commercial-roofing",
    "core:strata-roofing": "/strata-roofing",
    "core:residential-roofing": "/residential-roofing",
    "core:roof-systems": "/roof-systems",
    "core:roof-estimate": "/roof-estimate",
    "core:british-columbia": "/service-areas/british-columbia",
    "core:before-after": "/before-after",
    "core:blog": "/blog",
    "core:about": "/about",
    "core:faq": "/faq",
    "core:contact": "/contact",
    "core:privacy": "/privacy",
    "core:terms": "/terms",
    "core:thank-you": "/thank-you",
  };
  return map[routeId] ?? "/";
}

export function isCoreRouteId(routeId: RouteId): boolean {
  return CORE_ROUTE_IDS.has(routeId);
}

export function allRegisteredRouteIds(): RouteId[] {
  buildLookupTable();
  const ids = new Set<RouteId>();
  for (const entry of pathLookup.values()) {
    ids.add(entry.routeId);
  }
  return [...ids];
}

export function allLocalizedPaths(): { routeId: RouteId; locale: LocaleCode; path: string }[] {
  buildLookupTable();
  return [...pathLookup.entries()].map(([path, entry]) => ({
    routeId: entry.routeId,
    locale: entry.locale,
    path,
  }));
}

export { DIRECTORY_SEGMENTS, LOCALES, ROUTE_LOCALE_KEYS };
